import { useRef, useCallback, useEffect, useState, ChangeEvent, KeyboardEvent, forwardRef, useImperativeHandle } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { FiImage, FiSend, FiX } from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import { track } from '@/lib/analytics';
import { useTempCheck } from '@/hooks/useTempCheck';
import { TempCheckBar } from './TempCheckBar';

type TempCheckResult = {
  level: 'cool' | 'warm' | 'hot';
  score: number;
  label: string;
  suggestion: string | null;
};

type MessageInputProps = {
  messageText: string;
  onMessageTextChange: (text: string) => void;
  selectedImages: File[];
  onSelectedImagesChange: (images: File[]) => void;
  onSend: () => void;
  isSending: boolean;
  disabled?: boolean;
};

export interface MessageInputHandle {
  focus: () => void;
}

export const MessageInput = forwardRef<MessageInputHandle, MessageInputProps>(
  function MessageInput(
    {
      messageText,
      onMessageTextChange,
      selectedImages,
      onSelectedImagesChange,
      onSend,
      isSending,
      disabled = false,
    },
    ref
  ) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
    }));

    const { getToken } = useAuth();
    const { enabled: tempCheckEnabled } = useTempCheck();
    const [tempResult, setTempResult] = useState<TempCheckResult | null>(null);
    const [tempLoading, setTempLoading] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    }, []);

    const debouncedCheck = useCallback(
      (text: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!tempCheckEnabled) {
          setTempResult(null);
          setTempLoading(false);
          return;
        }
        const trimmed = text.trim();
        if (trimmed.length < 3) {
          setTempResult(null);
          setTempLoading(false);
          return;
        }
        setTempLoading(true);
        debounceRef.current = setTimeout(async () => {
          try {
            const result = await apiJson<TempCheckResult>(getToken, '/v1/tempcheck', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ content: trimmed }),
            });
            setTempResult(result);
            track('tone_check_result', {
              level: result.level,
              score: result.score,
              has_suggestion: !!result.suggestion,
            });
          } catch {
            setTempResult(null);
          } finally {
            setTempLoading(false);
          }
        }, 500);
      },
      [getToken, tempCheckEnabled]
    );

    const handleTextChange = useCallback(
      (text: string) => {
        onMessageTextChange(text);
        debouncedCheck(text);
      },
      [onMessageTextChange, debouncedCheck]
    );

    const handleUseSuggestion = useCallback(
      (suggestion: string) => {
        track('tone_check_rewrite_used', {
          level: tempResult?.level,
          score: tempResult?.score,
        });
        onMessageTextChange(suggestion);
        setTempResult(null);
        debouncedCheck(suggestion);
      },
      [onMessageTextChange, debouncedCheck, tempResult]
    );

    const handleImageSelect = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        const remaining = 3 - selectedImages.length;
        if (remaining <= 0) {
          alert('You can attach up to 3 images.');
          return;
        }

        const newImages = Array.from(files).slice(0, remaining);
        onSelectedImagesChange([...selectedImages, ...newImages]);

        // Reset input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      },
      [selectedImages, onSelectedImagesChange]
    );

    const removeImage = useCallback(
      (index: number) => {
        onSelectedImagesChange(selectedImages.filter((_, i) => i !== index));
      },
      [selectedImages, onSelectedImagesChange]
    );

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          const trimmed = messageText.trim();
          if ((trimmed || selectedImages.length > 0) && !isSending && !disabled) {
            onSend();
          }
        }
      },
      [messageText, selectedImages.length, isSending, disabled, onSend]
    );

    const handleSend = useCallback(() => {
      const trimmed = messageText.trim();
      if ((!trimmed && selectedImages.length === 0) || isSending || disabled) return;
      if (tempResult && (tempResult.level === 'warm' || tempResult.level === 'hot')) {
        track('tone_check_sent_with_warning', {
          level: tempResult.level,
          score: tempResult.score,
        });
      }
      setTempResult(null);
      onSend();
    }, [messageText, selectedImages.length, isSending, disabled, onSend, tempResult]);

    const canSend =
      (messageText.trim() || selectedImages.length > 0) && !isSending && !disabled;

    return (
      <div className='px-4 py-3 border-t border-gray-200 bg-sky-light'>
        {/* Temp Check bar */}
        {tempCheckEnabled && !disabled && (
          <TempCheckBar
            level={tempResult?.level}
            label={tempResult?.label}
            suggestion={tempResult?.suggestion}
            isLoading={tempLoading}
            tooShort={messageText.trim().length < 3}
            onUseSuggestion={handleUseSuggestion}
          />
        )}

        {/* Selected images preview */}
        {selectedImages.length > 0 && (
          <div className='flex gap-2 mb-2'>
            {selectedImages.map((file, index) => (
              <div key={index} className='relative w-15 h-15'>
                <img
                  src={URL.createObjectURL(file)}
                  alt={`Selected ${index + 1}`}
                  className='w-[60px] h-[60px] object-cover rounded-lg'
                />
                <button
                  onClick={() => removeImage(index)}
                  className='absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors'
                >
                  <FiX className='w-3 h-3 text-white' />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Input row */}
        <div className='flex items-end gap-2.5'>
          {/* Image picker button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={selectedImages.length >= 3 || disabled}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              selectedImages.length >= 3 || disabled
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-primary hover:bg-primary/10'
            }`}
          >
            <FiImage className='w-5 h-5' />
          </button>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type='file'
            accept='image/*'
            multiple
            onChange={handleImageSelect}
            className='hidden'
          />

          {/* Text input */}
          <textarea
            ref={textareaRef}
            value={messageText}
            onChange={(e) => handleTextChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={disabled ? 'Chat is closed' : 'Type a message...'}
            disabled={isSending || disabled}
            rows={1}
            className={`flex-1 px-3.5 py-2.5 rounded-[20px] border resize-none text-base leading-5 max-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary/30 ${
              disabled
                ? 'bg-gray-100 text-gray-400 border-gray-200'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
            style={{ minHeight: '40px' }}
          />

          {/* Send button */}
          <button
            onClick={handleSend}
            disabled={!canSend}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              canSend
                ? 'bg-primary text-white hover:bg-primary-hover'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSending ? (
              <div className='w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin' />
            ) : (
              <FiSend className='w-5 h-5' />
            )}
          </button>
        </div>
      </div>
    );
  }
);
